<script setup lang="ts">
import { computed, h, onBeforeMount, onMounted, ref } from 'vue';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { formatDate } from '@/utils/dateUtils';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { TOUR, TOUR_TYPE } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useBookComposable } from '@/components/book/useBookComposable';
import { useModalComposable } from '@/views/composable';
import { useCheckMaxPassengersComposable } from '@/components/book/useCheckMaxPassengersComposable';
import { useDialog } from 'primevue/usedialog';
import { useI18n } from 'vue-i18n';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import ColumnGroup from 'primevue/columngroup';
import DataTable from 'primevue/datatable';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import Row from 'primevue/row';
import TableColumn from 'primevue/column';
import DetailedOccupancy from '@/components/book/DetailedOccupancy.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PackagePaxName from '@/components/book/PackagePaxName.vue';

const props = defineProps<{
  isManagement: boolean;
  priorityStatus?: number | undefined;
}>();

const emits = defineEmits<{ openClickSubmit: [value: boolean] }>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);
const toast = useToast();
const dialog = useDialog();
const adminSetupStore = useAdminSetupStore();
const bookingQuoteStore = useBookingQuoteStore();
const submitWorksheetStore = useSubmitWorksheetStore();
const { checkMaxRoom } = useCheckMaxPassengersComposable();
const { isInvalidTourUpdate, hasEditBookPermission, hasEditOccupacyPermission, checkPriorityStatus, hasViewOccupacyPermission, calculateRoomTotals, handleTotalAdultsChildren, columnStyles, handleDataExport, renderFileNameExport } =
  useBookComposable();
const { handleOpenErrorMessage, showMinNotification } = useModalComposable();

const dataMultiDayTour = ref();
const expandedRows = ref<any>({});
const mdtEditingRows = ref([]);
const isShowAddPaxTour = ref(false);
const isLoading = ref(false);
const roomTotals: any = ref([]);
const quote = computed(() => bookingQuoteStore.quote);
const editingRows = ref([]);
const roomDataUpdated: any = ref([]);
const isShow = ref(true);
const isShowDetailedOccupancy = ref(true);
const isUpdateChildForm = ref(false);
const tourMinimumConfig = computed(() => adminSetupStore.minimumConfig?.tourConfig);

const formatDataMdt = (rooms: any, tourName: string, pIndex: number) => {
  const data: any[] = [];
  rooms.forEach((room: any, rIndex: number) => {
    const roomClone = cloneDeep(room);
    const obj = {
      index: `${pIndex}${rIndex}`,
      [`tourName${langCapitalize}`]: tourName,
      ...roomClone
    };
    data.push(obj);
  });
  return data;
};

const areAllRoomNumbersZeroTour = (quote: any) => {
  for (const packageItem of quote.multiDayTour) {
    for (const tour of packageItem.tours) {
      for (const room of tour.rooms) {
        const roomInfo = room.roomInfo;
        if (roomInfo.singleNo || roomInfo.doubleNo || roomInfo.tripleNo || roomInfo.quadNo || roomInfo.fiveInRoomNo || roomInfo.sixInRoomNo) {
          return false;
        }
      }
    }
  }
  return true;
};

const submitMdtQuote = (updatePassengers: boolean = false, isUpdateBooking: boolean = false) => {
  const payloadMdt = {};
  payloadMdt.qType = quote.value.qType;
  payloadMdt.multiDayTour = quote.value.multiDayTour;
  payloadMdt.expires = quote.value.expires;
  payloadMdt.linkedQuotes = quote.value.linkedQuotes;
  isLoading.value = true;
  const updateFunction = updatePassengers ? submitWorksheetStore.updatePassengers(payloadMdt, quote.value.sk) : submitWorksheetStore.handleUpdateWorkSheet(payloadMdt, quote.value.sk);
  updateFunction
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      isShowDetailedOccupancy.value = true;

      emits('openClickSubmit', areAllRoomNumbersZeroTour(payloadMdt));

      if (isUpdateBooking) {
        isUpdateChildForm.value = true;
        isShowAddPaxTour.value = true;
      }
      if (!updatePassengers && !isUpdateBooking) {
        isShowAddPaxTour.value = false;
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onRowEditSave = ({ newData, index }: any, rooms: any, dataCommon: any, indexTour: number) => {
  const dataCommonClone = cloneDeep(dataCommon);
  const dataCopy = { ...dataCommonClone };
  dataCopy.tours[indexTour].rooms[index] = { ...newData };

  const listRoom: any[] = [];
  isShowDetailedOccupancy.value = false;

  dataCopy.tours.forEach((item: any) => {
    item.rooms.forEach((room: any) => {
      listRoom.push(room.roomInfo);
    });
  });
  const overAdult = checkMaxRoom(listRoom, dataCopy.tours[indexTour].adtNo, dataCopy.tours[indexTour].numOfRooms, indexTour);
  if (overAdult) {
    isShowDetailedOccupancy.value = true;
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_room'));
  } else {
    rooms[index].roomInfo = cloneDeep(newData.roomInfo);
    submitMdtQuote();
  }
};

const handleDataSaveTour = (dialogTourRef: any) => {
  isUpdateChildForm.value = false;
  dialogTourRef.close({ buttonType: 'Save', data: dialogTourRef.data.occupancy });
};

const addPax = (room: any, mdtIndex: number, tourIndex: number, roomIndex: number, tourData: any) => {
  const total = room.roomInfo?.singleNo || room.roomInfo?.doubleNo || room.roomInfo?.tripleNo || room.roomInfo?.quadNo || room.roomInfo?.fiveInRoomNo || room.roomInfo?.sixInRoomNo;
  const dialogTourRef = dialog.open(PackagePaxName, {
    data: {
      data: room,
      qType: TOUR,
      isManagement: props.isManagement,
      priorityStatus: props.priorityStatus,
      quoteId: quote.value?.sk,
      retDate: tourData.retDate,
      isMdt: true,
      gType: quote.value.gType,
      isUpdateChildForm: isUpdateChildForm.value
    },
    props: {
      maximizable: true,
      header: t('book_form.rooming_list'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    },
    templates: {
      header: () => {
        return [h('div', { class: 'align-items-center flex' }, [t('booking.hint_header'), h('i', { class: 'fa-solid fa-hand-point-right ml-3 text-4xl' })])];
      },
      footer: () => {
        if (hasEditOccupacyPermission) {
          return [
            h(Button, {
              disabled: !total,
              autofocus: true,
              class: 'mt-4',
              label: t('common.save'),
              icon: 'pi pi-check',
              onClick: () => {
                handleDataSaveTour(dialogTourRef);
              }
            })
          ];
        }
      }
    },
    onClose: ({ data: dialogData }) => {
      handleOnClose(dialogData, mdtIndex, tourIndex, roomIndex);
    }
  });
};

const handleOnClose = (dialogData: any, mdtIndex: number, tourIndex: number, roomIndex: number) => {
  if (dialogData) {
    isShow.value = false;
    document.getElementsByTagName('body')[0].classList.add('p-overflow-hidden-2');
    const roomData = cloneDeep(dialogData.data);
    roomData?.forEach((occItem: any) => {
      dataMultiDayTour.value[mdtIndex].tours[tourIndex].rooms[roomIndex].roomInfo[`${occItem.field}RoomInfos`] = occItem.passengers.map((p: any) => {
        return {
          adtPassengers: p.people,
          chdPassengers: p.children || [],
          infPassengers: p.infants || [],
          email: p.email,
          roomRequests: p.roomRequests
        };
      });
    });
    submitMdtQuote(true);
    isShow.value = true;
  }
};

const onRowEditSaveMdt = (event: any, mdtIndex: number, tour: any, tourIndex: number) => {
  const tourTemp = dataMultiDayTour.value[mdtIndex].tours[tourIndex];
  const isChange = tourTemp.adtNo !== event.newData.adtNo || tourTemp.numOfRooms !== event.newData.numOfRooms;
  if (!quote.value.isReplicate && isInvalidTourUpdate(dataMultiDayTour.value, mdtIndex, tourIndex, event.newData, tourMinimumConfig.value)) {
    showMinNotification(t('advance_quote.form.min_pax_package_book', { minPax: tourMinimumConfig.value.minPax, minRoom: tourMinimumConfig.value.minRoomsCabins }));
  } else {
    dataMultiDayTour.value[mdtIndex].tours[tourIndex].adtNo = event.newData.adtNo;
    dataMultiDayTour.value[mdtIndex].tours[tourIndex].chdNo = event.newData.chdNo;
    dataMultiDayTour.value[mdtIndex].tours[tourIndex].numOfRooms = event.newData.numOfRooms;
    if (isChange) {
      tour.rooms.forEach((room: any) => {
        room.roomInfo.singleNo = 0;
        room.roomInfo.doubleNo = 0;
        room.roomInfo.tripleNo = 0;
        room.roomInfo.quadNo = 0;
        room.roomInfo.fiveInRoomNo = 0;
        room.roomInfo.sixInRoomNo = 0;
      });
      toast.add({ severity: 'info', summary: '', detail: t('notification.info_delete_room_entered'), life: 5000 });
    }
    submitMdtQuote();
  }
};

onBeforeMount(() => {
  dataMultiDayTour.value = quote.value.multiDayTour;
  dataMultiDayTour.value?.forEach((mdt: any, mdtIndex: number) => {
    mdt.key = `Mdt${mdtIndex}`;
    mdt.tours.forEach((tour: any, tourIndex: number) => {
      tour.key = `Tour${mdtIndex}${tourIndex}`;
      tour.rooms.forEach((room: any, roomIndex: number) => {
        room.roomKey = `Room${mdtIndex}${tourIndex}${roomIndex}`;
        roomDataUpdated.value.push({
          mdtIndex,
          tourIndex,
          roomIndex
        });
      });
    });
  });
});

const formatBookingId = (bookingIds: any) => {
  if (bookingIds) {
    return bookingIds.replace(/,/g, '\n');
  }
};

const updateResult = (data: any) => {
  const resultTotals = calculateRoomTotals(data.event);
  const newEntry = {
    mdtIndex: data.mdtIndex,
    tourIndex: data.tourIndex,
    roomIndex: data.roomIndex,
    ...resultTotals
  };

  const existingIndex = roomTotals.value.findIndex((item: any) => item.mdtIndex === newEntry.mdtIndex && item.tourIndex === newEntry.tourIndex && item.roomIndex === newEntry.roomIndex);

  if (existingIndex > -1) {
    roomTotals.value[existingIndex] = newEntry;
  } else {
    roomTotals.value.push(newEntry);
  }
};

const calculateTotalRoom = (mdtIndex: number, tourIndex: number, roomIndex: number) => {
  const roomInfo = dataMultiDayTour.value[mdtIndex]?.tours[tourIndex]?.rooms[roomIndex]?.roomInfo;
  if (roomInfo) {
    let totalRoom = 0;
    for (const key in roomInfo) {
      if (key.endsWith('No')) {
        totalRoom += roomInfo[key];
      }
    }
    if (totalRoom) {
      isShowAddPaxTour.value = true;
    }
    return totalRoom;
  }

  return 0;
};

const handleSaveRoomDataClone = (data: any) => {
  const newEntry = {
    mdtIndex: data.mdtIndex,
    tourIndex: data.tourIndex,
    roomIndex: data.roomIndex,
    ...data.event
  };
  const existingIndex = roomDataUpdated.value.findIndex((item: any) => item.mdtIndex === newEntry.mdtIndex && item.tourIndex === newEntry.tourIndex && item.roomIndex === newEntry.roomIndex);
  roomDataUpdated.value[existingIndex] = newEntry;
};

const updateBookingRequestForm = (event: any, room: any, mdtIndex: number, tourIndex: number, roomIndex: number) => {
  updateResult({ event, mdtIndex, tourIndex, roomIndex });
  handleSaveRoomDataClone({ event, mdtIndex, tourIndex, roomIndex });
};

const handleFindRoom = (mdtIndex: number, tourIndex: number, roomIndex: number) => {
  return roomDataUpdated.value.find((item: any) => item.mdtIndex === mdtIndex && item.tourIndex === tourIndex && item.roomIndex === roomIndex);
};

const checkOccupancy = (isUpdate?: boolean) => {
  let isValid: boolean = true;
  dataMultiDayTour.value.forEach((element: any, mdtIndex: number) => {
    const { tours } = element;
    tours.forEach((tour: any, tourIndex: number) => {
      const { adtNo, chdNo } = tour;
      const totalAllowed = adtNo + chdNo;
      let totalOccupants = 0;
      tour.rooms.forEach((room: any, roomIndex: number) => {
        const occupancy = handleFindRoom(mdtIndex, tourIndex, roomIndex);
        if (isUpdate) {
          room.detailedOccupancy = occupancy || room.detailedOccupancy;
        } else {
          totalOccupants = totalOccupants + handleTotalAdultsChildren(occupancy);
        }
      });
      if (totalOccupants > totalAllowed) {
        isValid = false;
      }
    });
  });
  return isValid;
};

const getPersonTotal = (mdtIndex: number, tourIndex: number, roomIndex: number, roomType: string, personType: string) => {
  if (roomTotals.value && roomTotals.value.length) {
    return roomTotals.value.find((item: any) => item.mdtIndex === mdtIndex && item.tourIndex === tourIndex && item.roomIndex === roomIndex)?.[roomType]?.[personType] ?? 0;
  }
  return 0;
};

const updateBookingRequest = () => {
  if (!checkOccupancy()) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_room'));
    return;
  }
  checkOccupancy(true);
  submitMdtQuote(false, true);
};

const handleHeaderLead = () => {
  const gType = quote.value.gType;
  return gType.toUpperCase() === 'WEDDING' ? t('book_form.bride_groom') : t('book_form.group_leader');
};

onMounted(() => {
  dataMultiDayTour.value.forEach((element: any) => {
    expandedRows.value = { ...expandedRows.value, [element.key]: true };
  });
  emits('openClickSubmit', areAllRoomNumbersZeroTour(quote.value));
});
</script>

<template>
  <div class="booking-option-wrap">
    <LoadingSpinner v-if="isLoading" />
    <div>
      <DataTable v-if="isShow" :value="dataMultiDayTour" v-model:expandedRows="expandedRows" dataKey="key">
        <template #header>
          <div class="grid">
            <div class="col">
              <h5>
                {{ $t('book_form.occupancy') }}
                <br />
                <small>{{ $t('book_form.quote_no') }}.: {{ quote.sk }}</small>
                <br />
                <small class="pre-line">{{ $t('book_form.booking_no') }}.: {{ formatBookingId(quote.bookingId) }}</small>
              </h5>
            </div>
            <div class="col">
              <p>
                {{ $t('advance_quote.form.group_name') }}: {{ quote.gName }}
                <br />
                {{ $t('advance_quote.form.group_type') }}: {{ getTextVariant(currentLanguage, quote.gType, quote.gTypeFr) || '-' }}
                <br />
                {{ $t('advance_quote.form.group_contact') }}: {{ quote.gContact }}
              </p>
              <div class="align-items-center hidden">
                <label for="tour-emergency-phone">{{ $t('book_form.emergency_phone') }}</label>
                <div class="col agency-phone-class">
                  <InputMask id="tour-emergency-phone" :mask="$t('common.labels.emergency_phone_mask')" v-model="quote.agencyPhone" :placeholder="$t('common.labels.emergency_phone_number')" />
                </div>
              </div>
            </div>
            <div class="col">
              <div class="export-booking">
                <ExportExcelFile v-slot="slotProps" :fileName="renderFileNameExport(quote.sk, quote?.gName)" :isAoaToSheet="true" :columnsStyle="columnStyles">
                  <Button icon="pi pi-external-link" @click="handleDataExport(slotProps.export, dataMultiDayTour, isManagement, handleHeaderLead(), TOUR_TYPE, 'tours')" :label="$t('common.buttons.export_rooming_list')" class="export-booking-btn" />
                </ExportExcelFile>
              </div>
            </div>
          </div>
        </template>
        <TableColumn :expander="true" class="w-3rem" />
        <TableColumn field="option" :header="$t('book_form.option')">
          <template #body="slotProps"> {{ $t('book_form.option') }} {{ slotProps.index + 1 }} </template>
        </TableColumn>
        <TableColumn :field="getTextVariant(currentLanguage, 'gate', 'gateFr')" :header="$t('book_form.from')"> </TableColumn>
        <TableColumn :field="getTextVariant(currentLanguage, 'dest', 'destFr')" :header="$t('book_form.to')"> </TableColumn>
        <template #expansion="{ data, index: mdtIndex }">
          <div v-for="(tour, tourIndex) in data.tours" :key="'tour' + tourIndex">
            <DataTable :value="[data.tours[tourIndex]]" dataKey="key" editMode="row" v-model:editingRows="mdtEditingRows" @row-edit-save="onRowEditSaveMdt($event, mdtIndex, tour, tourIndex)">
              <TableColumn field="">
                <template #header>
                  <p>{{ $t('management.tour.tour_name') }}: {{ tour[`tourName${langCapitalize}`] }}</p>
                </template>
              </TableColumn>
              <TableColumn field="depDate" :header="$t('book_form.departure')">
                <template #body="{ data }">
                  {{ formatDate(data.depDate, 'dd-LLL-yyyy', true, currentLanguage) }}
                </template>
              </TableColumn>
              <TableColumn field="adtNo" :header="$t('book_form.adults')">
                <template #editor="{ data, field }">
                  <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
                </template>
              </TableColumn>
              <TableColumn field="chdNo" :header="$t('book_form.children')">
                <template #editor="{ data, field }">
                  <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
                </template>
              </TableColumn>
              <TableColumn field="numOfRooms" :header="$t('book_form.number_of_rooms')">
                <template #editor="{ data, field }">
                  <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
                </template>
              </TableColumn>
              <TableColumn v-if="isManagement" :header="$t('common.edit')" :rowEditor="true" class="py-3"></TableColumn>
            </DataTable>
            <DataTable
              :value="formatDataMdt(tour.rooms, tour[`tourName${langCapitalize}`], tourIndex)"
              editMode="row"
              dataKey="roomKey"
              v-model:editingRows="editingRows"
              @row-edit-save="onRowEditSave($event, tour.rooms, data, tourIndex)"
              class="editable-cells-table bg-blue-50"
              responsiveLayout="stack"
            >
              <ColumnGroup type="header">
                <Row>
                  <TableColumn field="room" :header="$t('book_form.room_category')" :rowspan="1" />
                  <TableColumn v-if="tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" :header="$t('book_form.single')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn v-if="tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" :header="$t('book_form.double')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn v-if="tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" :header="$t('book_form.triple')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn v-if="tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" :header="$t('book_form.quad')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn v-if="tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" :header="$t('book_form.five')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn v-if="tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" :header="$t('book_form.six')" :colspan="isShowAddPaxTour ? 3 : 1" />
                  <TableColumn :header="$t('common.edit')" :colspan="1" />
                  <TableColumn :header="$t('common.action')" :colspan="1" />
                </Row>
                <Row v-if="isShowAddPaxTour">
                  <TableColumn :colspan="1" />
                  <TableColumn v-if="tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" :header="$t('book_form.pound_sign_rooms')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" :header="$t('book_form.pound_sign_children')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn v-if="tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" :header="$t('book_form.pound_sign_infants')" class="width-column-group room-font-size"> </TableColumn>
                  <TableColumn :colspan="1" />
                  <TableColumn :colspan="1" />
                </Row>
              </ColumnGroup>
              <TableColumn :field="getTextVariant(currentLanguage, 'longName', 'longNameFr')" :header="$t('book_form.room_category')" class="py-3 room-name-column"> </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" field="singleNo" :header="$t('book_form.single')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 1 || data.maxPaxAdult < 1" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'singleRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 1 && tour.maxPaxRoom >= 1" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'singleRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" field="doubleNo" :header="$t('book_form.double')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 2 || data.maxPaxAdult < 2" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'doubleRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 2 && tour.maxPaxRoom >= 2" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'doubleRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" field="tripleNo" :header="$t('book_form.triple')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 3 || data.maxPaxAdult < 3" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'tripleRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 3 && tour.maxPaxRoom >= 3" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'tripleRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" field="quadNo" :header="$t('book_form.quad')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 4 || data.maxPaxAdult < 4" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'quadRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 4 && tour.maxPaxRoom >= 4" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'quadRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" field="fiveInRoomNo" :header="$t('book_form.five')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 5 || data.maxPaxAdult < 5" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'fiveRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 5 && tour.maxPaxRoom >= 5" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'fiveRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" field="sixInRoomNo" :header="$t('book_form.six')">
                <template #editor="{ data, field }">
                  <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxAdult > 6 || data.maxPaxAdult < 6" autofocus :useGrouping="false" />
                </template>
                <template #body="{ data, field }">
                  <p>{{ data.roomInfo[field] ?? 0 }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 5" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'sixRoom', 'children') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="isShowAddPaxTour && tour.minPaxRoom <= 6 && tour.maxPaxRoom >= 6" class="width-column-group">
                <template #body="{ index }">
                  <p>{{ getPersonTotal(mdtIndex, tourIndex, index, 'sixRoom', 'infants') }}</p>
                </template>
              </TableColumn>
              <TableColumn v-if="hasEditBookPermission && !checkPriorityStatus(priorityStatus, isManagement)" :header="$t('common.edit')" :rowEditor="true" class="py-3 w-8rem edit-row"></TableColumn>
              <TableColumn v-if="hasViewOccupacyPermission" :header="$t('common.action')" class="py-3">
                <template #body="{ data: dataRoom, index }">
                  <Button
                    id="add-pax-id"
                    v-if="calculateTotalRoom(mdtIndex, tourIndex, index)"
                    :label="isManagement ? $t('book_form.edit_pax') : $t('book_form.add_pax')"
                    @click="addPax(dataRoom, mdtIndex, tourIndex, index, data)"
                    class="p-button-outlined edit-pax"
                  />
                </template>
              </TableColumn>
            </DataTable>
            <div v-if="isShowDetailedOccupancy">
              <div v-for="(room, roomIndex) in tour.rooms" :key="roomIndex" class="mt-3">
                <DetailedOccupancy isMdt :hotel="tour" :room="room" :detailedOccupancy="room?.detailedOccupancy" @updateBookingRequestForm="updateBookingRequestForm($event, room, mdtIndex, tourIndex, roomIndex)"></DetailedOccupancy>
              </div>
            </div>
          </div>
        </template>
      </DataTable>
      <div class="flex justify-content-end mt-3">
        <Button id="update-booking-request" @click="updateBookingRequest()" :label="$t('book_form.update_booking_request_form')" icon="pi pi-check" />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.pre-line {
  white-space: pre-line;
}

:deep() {
  .room-font-size {
    font-size: 10px;
  }
}
</style>
